import { Alert } from "antd";
import { Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetGradingLockStatus } from "@hooks";
import { useEffect } from "react";
import dayjs from "dayjs";

interface Props {
  examInstanceId: string;
  onLockedByOther?: (isLocked: boolean) => void;
}

export const GradingLockIndicator = ({ examInstanceId, onLockedByOther }: Props) => {
  const { t } = useTranslation();
  const { data: lockStatus, isLoading } = useGetGradingLockStatus(examInstanceId, {
    refetchInterval: 15000, // Check every 15 seconds
  });

  useEffect(() => {
    if (lockStatus && onLockedByOther) {
      onLockedByOther(lockStatus.isLocked && !lockStatus.isLockedByCurrentAssessor);
    }
  }, [lockStatus, onLockedByOther]);

  if (isLoading || !lockStatus) {
    return null;
  }

  // If locked by current assessor, show success indicator
  if (lockStatus.isLockedByCurrentAssessor) {
    return (
      <Alert
        message={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Lock size={16} />
            <span>{t("grading.lockedByYou")}</span>
            {lockStatus.lockedAt && (
              <span style={{ fontSize: "12px", opacity: 0.7 }}>
                ({dayjs(lockStatus.lockedAt).format("HH:mm")})
              </span>
            )}
          </div>
        }
        type="success"
        showIcon={false}
        style={{ marginBottom: 16 }}
      />
    );
  }

  // If locked by another assessor, show warning
  if (lockStatus.isLocked) {
    return (
      <Alert
        message={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Lock size={16} />
            <span>
              {t("grading.lockedByOther", { name: lockStatus.lockedByAssessorName })}
            </span>
            {lockStatus.lockedAt && (
              <span style={{ fontSize: "12px", opacity: 0.7 }}>
                ({dayjs(lockStatus.lockedAt).fromNow()})
              </span>
            )}
          </div>
        }
        type="warning"
        showIcon={false}
        style={{ marginBottom: 16 }}
      />
    );
  }

  // Not locked - no indicator needed
  return null;
};
